import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { privateRequest } from "services/privateRequest";
import { IMPORT_TEST_CASES, IMPORT_TEST_CASES_EXPORT_LOG, IMPORT_TEST_CASES_LOGS, TEST_CASES } from "services/endPoints";

const initialState = {
  getTestCasesList: [],
  testCaseDetails: null,
  testCaseLoading: false,
  testCaseDetailsLoading: false,
  deleteTestCaseLoading: false,
  createTestCaseLoading: false,
  updateTestCaseLoading: false,
  testCaseTotalCount: 0,
  /** Per-section paginated cases: { [sectionId]: { list, total, page, size, loading, loadingMore, loaded } } */
  sectionCasesMap: {},
  importLogsList: [],
  importLogsTotalCount: 0,
  importLogsLoading: false,
  exportImportLogLoading: false,
};

const downloadBlobResponse = (response, fallbackName = "export.xlsx") => {
  const contentDisposition = response?.headers?.["content-disposition"];
  let fileName = fallbackName;
  if (contentDisposition) {
    const match = String(contentDisposition).match(
      /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i
    );
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1].replace(/['"]/g, "").trim());
    }
  }
  const url = window.URL.createObjectURL(new Blob([response?.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const normalizePagedList = (res) => {
  const responseData = res?.data?.data ?? res?.data ?? {};
  const data = Array.isArray(responseData?.data)
    ? responseData.data
    : Array.isArray(responseData?.content)
      ? responseData.content
      : Array.isArray(responseData)
        ? responseData
        : Array.isArray(res?.data)
          ? res.data
          : [];
  const total =
    responseData?.count?.total ??
    responseData?.count?.Total ??
    responseData?.totalElements ??
    responseData?.total ??
    data.length;
  return { data, total: Number(total) || 0 };
};

/** Used by Test Runs / selects — do not pass sectionId here for global list. */
export const getTestCases = createAsyncThunk(
  "testCase/getTestCases",
  async (
    { pageNo = 1, pageSize = 20, search = "", append: _append, ...rest },
    { rejectWithValue }
  ) => {
    try {
      // Swagger GET /test-cases: page + size (not pageNo/pageSize)
      const res = await privateRequest.get(TEST_CASES, {
        params: {
          page: pageNo,
          size: pageSize,
          search,
          ...rest,
        },
      });
      const responseData = res?.data?.data || {};
      const data = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData?.content)
          ? responseData.content
          : Array.isArray(responseData)
            ? responseData
            : [];
      const total =
        responseData?.count?.total ??
        responseData?.count?.Total ??
        responseData?.totalElements ??
        responseData?.total ??
        data.length;
      return { data, count: { total: Number(total) || 0 } };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

/** Lazy load for Test Cases accordion: GET /test-cases?sectionId=&page=&size= */
export const getTestCasesBySection = createAsyncThunk(
  "testCase/getTestCasesBySection",
  async (
    {
      sectionId,
      page = 1,
      size = 20,
      search = "",
      append = false,
      sortBy = "id",
      direction = "desc",
      issueId,
      issueType,
      assigneeId,
    },
    { rejectWithValue }
  ) => {
    try {
      if (sectionId == null || sectionId === "") {
        return rejectWithValue({ message: "sectionId is required" });
      }
      const res = await privateRequest.get(TEST_CASES, {
        params: {
          sectionId,
          page,
          size,
          search,
          sortBy,
          direction,
          ...(issueId != null && issueId !== "" ? { issueId } : {}),
          ...(issueType != null && issueType !== "" ? { issueType } : {}),
          ...(assigneeId != null && assigneeId !== "" ? { assigneeId } : {}),
        },
      });
      const responseData = res?.data?.data || {};
      const data = responseData.data || [];
      const count = responseData.count || {};
      const total =
        count?.total ??
        count?.Total ??
        responseData?.total ??
        data.length;
      return {
        sectionId: String(sectionId),
        data,
        total: Number(total) || 0,
        page: Number(page) || 1,
        size: Number(size) || 20,
        append: Boolean(append),
      };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getTestCaseById = createAsyncThunk(
  "testCase/getTestCaseById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await privateRequest.get(`${TEST_CASES}/${id}`);
      return res?.data?.data || res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const createTestCase = createAsyncThunk(
  "testCase/createTestCase",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await privateRequest.post(TEST_CASES, payload);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Test case created successfully."
        );
      }
      return res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateTestCase = createAsyncThunk(
  "testCase/updateTestCase",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await privateRequest.put(`${TEST_CASES}/${id}`, payload);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Test case updated successfully."
        );
      }
      return res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

/** Bulk import test cases from CSV / Excel (same pattern as importBulkIssue). */
export const importBulkTestCases = createAsyncThunk(
  "testCase/importBulkTestCases",
  async ({ file, projectId }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (projectId != null && projectId !== "") {
        formData.append("projectId", projectId);
      }
      await privateRequest.post(IMPORT_TEST_CASES, formData);
      toast.success("File imported Successfully");
    } catch (error) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

/** GET /api/test-cases/bulk-upload/logs */
export const getTestCaseImportLogs = createAsyncThunk(
  "testCase/getTestCaseImportLogs",
  async (
    { page = 1, size = 10, projectId, append = false } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await privateRequest.get(IMPORT_TEST_CASES_LOGS, {
        params: {
          page,
          size,
          ...(projectId != null && projectId !== "" ? { projectId } : {}),
        },
      });
      const { data, total } = normalizePagedList(res);
      return { data, total, append };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

/** GET /api/test-cases/bulk-upload/export-log/{id} */
export const exportTestCaseImportLog = createAsyncThunk(
  "testCase/exportTestCaseImportLog",
  async (logId, { rejectWithValue }) => {
    try {
      if (logId == null || logId === "") {
        return rejectWithValue({ message: "logId is required" });
      }
      const response = await privateRequest.get(
        IMPORT_TEST_CASES_EXPORT_LOG(logId),
        { responseType: "blob" }
      );
      downloadBlobResponse(
        response,
        `test-case-import-log-${logId}.xlsx`
      );
      toast.success("Log exported successfully");
      return logId;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const deleteTestCase = createAsyncThunk(
  "testCase/deleteTestCase",
  async (id, { rejectWithValue }) => {
    try {
      const res = await privateRequest.delete(`${TEST_CASES}/${id}`);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Test case deleted successfully."
        );
      }
      return { id, data: res?.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const emptySectionState = () => ({
  list: [],
  total: 0,
  page: 0,
  size: 20,
  loading: false,
  loadingMore: false,
  loaded: false,
});

const testCaseSlice = createSlice({
  name: "testCase",
  initialState,
  reducers: {
    clearTestCaseDetails: (state) => {
      state.testCaseDetails = null;
    },
    clearSectionCasesMap: (state) => {
      state.sectionCasesMap = {};
    },
    invalidateSectionCases: (state, action) => {
      const key = String(action.payload);
      if (state.sectionCasesMap[key]) {
        delete state.sectionCasesMap[key];
      }
    },
    hydrateSectionCasesMap: (state, action) => {
      const grouped = action.payload || {};
      const next = {};
      Object.entries(grouped).forEach(([sectionId, list]) => {
        const rows = Array.isArray(list) ? list : [];
        next[String(sectionId)] = {
          list: rows,
          total: rows.length,
          page: 1,
          size: rows.length || 20,
          loading: false,
          loadingMore: false,
          loaded: true,
        };
      });
      state.sectionCasesMap = next;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTestCases.pending, (state, action) => {
      // Keep dropdown usable while loading more pages
      if (!action.meta?.arg?.append) {
        state.testCaseLoading = true;
      }
    });
    builder.addCase(getTestCases.fulfilled, (state, action) => {
      state.testCaseLoading = false;
      const append = Boolean(action.meta?.arg?.append);
      const incoming = action.payload.data || [];
      if (append) {
        const existingIds = new Set(
          (state.getTestCasesList || []).map((item) => String(item?.id))
        );
        state.getTestCasesList = [
          ...(state.getTestCasesList || []),
          ...incoming.filter((item) => !existingIds.has(String(item?.id))),
        ];
      } else {
        state.getTestCasesList = incoming;
      }
      state.testCaseTotalCount = action.payload.count?.total || 0;
    });
    builder.addCase(getTestCases.rejected, (state) => {
      state.testCaseLoading = false;
    });

    builder.addCase(getTestCasesBySection.pending, (state, action) => {
      const sectionId = String(action.meta?.arg?.sectionId ?? "");
      const append = Boolean(action.meta?.arg?.append);
      if (!sectionId) return;
      const prev = state.sectionCasesMap[sectionId] || emptySectionState();
      state.sectionCasesMap[sectionId] = {
        ...prev,
        loading: !append,
        loadingMore: append,
      };
    });
    builder.addCase(getTestCasesBySection.fulfilled, (state, action) => {
      const { sectionId, data, total, page, size, append } = action.payload;
      const prev = state.sectionCasesMap[sectionId] || emptySectionState();
      const nextList = append ? [...(prev.list || []), ...(data || [])] : data || [];
      state.sectionCasesMap[sectionId] = {
        list: nextList,
        total,
        page,
        size,
        loading: false,
        loadingMore: false,
        loaded: true,
      };
    });
    builder.addCase(getTestCasesBySection.rejected, (state, action) => {
      const sectionId = String(action.meta?.arg?.sectionId ?? "");
      if (!sectionId) return;
      const prev = state.sectionCasesMap[sectionId] || emptySectionState();
      state.sectionCasesMap[sectionId] = {
        ...prev,
        loading: false,
        loadingMore: false,
        loaded: true,
      };
    });

    builder.addCase(getTestCaseById.pending, (state) => {
      state.testCaseDetailsLoading = true;
    });
    builder.addCase(getTestCaseById.fulfilled, (state, action) => {
      state.testCaseDetailsLoading = false;
      state.testCaseDetails = action.payload;
    });
    builder.addCase(getTestCaseById.rejected, (state) => {
      state.testCaseDetailsLoading = false;
    });

    builder.addCase(createTestCase.pending, (state) => {
      state.createTestCaseLoading = true;
    });
    builder.addCase(createTestCase.fulfilled, (state) => {
      state.createTestCaseLoading = false;
    });
    builder.addCase(createTestCase.rejected, (state) => {
      state.createTestCaseLoading = false;
    });

    builder.addCase(updateTestCase.pending, (state) => {
      state.updateTestCaseLoading = true;
    });
    builder.addCase(updateTestCase.fulfilled, (state) => {
      state.updateTestCaseLoading = false;
    });
    builder.addCase(updateTestCase.rejected, (state) => {
      state.updateTestCaseLoading = false;
    });

    builder.addCase(deleteTestCase.pending, (state) => {
      state.deleteTestCaseLoading = true;
    });
    builder.addCase(deleteTestCase.fulfilled, (state) => {
      state.deleteTestCaseLoading = false;
    });
    builder.addCase(deleteTestCase.rejected, (state) => {
      state.deleteTestCaseLoading = false;
    });

    builder.addCase(getTestCaseImportLogs.pending, (state, action) => {
      if (!action.meta?.arg?.append) {
        state.importLogsLoading = true;
      }
    });
    builder.addCase(getTestCaseImportLogs.fulfilled, (state, action) => {
      state.importLogsLoading = false;
      const append = Boolean(action.payload?.append);
      const incoming = action.payload?.data || [];
      if (append) {
        const existingIds = new Set(
          (state.importLogsList || []).map((item) => String(item?.id))
        );
        state.importLogsList = [
          ...(state.importLogsList || []),
          ...incoming.filter((item) => !existingIds.has(String(item?.id))),
        ];
      } else {
        state.importLogsList = incoming;
      }
      state.importLogsTotalCount = action.payload?.total || 0;
    });
    builder.addCase(getTestCaseImportLogs.rejected, (state) => {
      state.importLogsLoading = false;
    });

    builder.addCase(exportTestCaseImportLog.pending, (state) => {
      state.exportImportLogLoading = true;
    });
    builder.addCase(exportTestCaseImportLog.fulfilled, (state) => {
      state.exportImportLogLoading = false;
    });
    builder.addCase(exportTestCaseImportLog.rejected, (state) => {
      state.exportImportLogLoading = false;
    });
  },
});

export const {
  clearTestCaseDetails,
  clearSectionCasesMap,
  invalidateSectionCases,
  hydrateSectionCasesMap,
} = testCaseSlice.actions;

export default testCaseSlice.reducer;
